import { useEffect, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AtSign,
  Check,
  CheckCheck,
  ListFilter,
  Lock,
  MessageCircle,
  PencilLine,
  Search,
  SquarePen,
  X,
} from 'lucide-react';
import { Link, NavLink } from 'react-router';
import type { ThreadSummary } from '@rundown/types';
import { LoadMore } from '../../components';
import { cx } from '../../lib/cx';
import { queryKeys } from '../../lib/queryKeys';
import { formatRelativeTime } from '../notifications/relativeTime';
import { useCapabilities } from '../workspace/useCapability';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { NewRequestDialog } from '../requests/NewRequestDialog';
import { STATUS_LABELS, TYPE_TONES } from '../requests/labels';
import {
  markAllThreadsRead,
  threadCountsQueryOptions,
  threadsInfiniteQueryOptions,
  type ThreadFilter,
} from './api';
import { ActionContextMenu, ActionMenuButton, useThreadActions } from './ThreadMenu';

/** The three views people switch between all day. */
const PRIMARY: readonly { readonly value: ThreadFilter; readonly label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'mentions', label: 'Mentions' },
];

/** Narrower cuts, one menu away. */
const SECONDARY: readonly {
  readonly value: ThreadFilter;
  readonly label: string;
  readonly reviewOnly?: boolean;
}[] = [
  { value: 'chats', label: 'Chats' },
  { value: 'issues', label: 'Issues' },
  { value: 'following', label: 'Following' },
  { value: 'review', label: 'Needs review', reviewOnly: true },
  { value: 'archived', label: 'Archived' },
];

/** The type's colour, on the number itself — no pill behind it. */
const TONE_TEXT: Record<string, string> = {
  neutral: 'text-muted',
  accent: 'text-accent',
  success: 'text-success',
  warn: 'text-warn',
  danger: 'text-danger',
  violet: 'text-violet',
};

const TONE_BADGE: Record<string, string> = {
  neutral: 'bg-surface-3 text-muted',
  accent: 'bg-accent-tint text-accent',
  success: 'bg-success-tint text-success',
  warn: 'bg-warn-tint text-warn',
  danger: 'bg-danger-tint text-danger',
  violet: 'bg-violet-tint text-violet',
};

const FILTER_STORAGE_KEY = 'rundown.threads.filter';

const ICON_BUTTON = 'grid size-7 place-items-center rounded-md text-muted hover:bg-surface-3 hover:text-ink';

function initialFilter(canReview: boolean): ThreadFilter {
  try {
    const stored = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (PRIMARY.some((option) => option.value === stored)) return stored as ThreadFilter;
    const secondary = SECONDARY.find((option) => option.value === stored);
    if (secondary !== undefined && (secondary.reviewOnly !== true || canReview)) return secondary.value;
  } catch {
    // Storage blocked — the default is fine.
  }
  return 'all';
}

/** Waits for typing to pause before searching, so each keystroke is not a request. */
function useDebounced(value: string, delayMs: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/**
 * The thread list: every chat and issue the signed-in person can see, newest
 * activity first.
 *
 * Kept deliberately quiet. The one thing allowed to shout is what is new to
 * you — a dot and a bold title. Everything else (the number, the snippet, the
 * status) is set back, so a list of forty threads reads as forty lines rather
 * than forty badges. Chats and issues are still told apart at a glance: a
 * speech bubble, or the issue number in its type's colour.
 *
 * All / Unread / Mentions are the views people move between all day; the
 * narrower cuts (chats only, archived, the review queue) are one menu away.
 */
export function ThreadSidebar() {
  const { workspace } = useRequiredWorkspace();
  const capabilities = useCapabilities();
  const canReview = capabilities.has('request.review');
  const queryClient = useQueryClient();
  const [filter, setFilterState] = useState<ThreadFilter>(() => initialFilter(canReview));
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search.trim(), 200);

  const setFilter = (next: ThreadFilter) => {
    setFilterState(next);
    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, next);
    } catch {
      // Remembering the view is a convenience, not a requirement.
    }
  };

  const query = useInfiniteQuery(threadsInfiniteQueryOptions(workspace.id, filter, debounced));
  const threads = query.data?.pages.flatMap((page) => page.items) ?? [];
  const counts = useQuery(threadCountsQueryOptions(workspace.id)).data;
  const unreadCount = counts?.unread ?? 0;
  const mentionCount = counts?.mentions ?? 0;
  const secondaryOptions = SECONDARY.filter((option) => option.reviewOnly !== true || canReview);
  const activeSecondary = secondaryOptions.find((option) => option.value === filter);

  const readAll = useMutation({
    mutationFn: () => markAllThreadsRead(workspace.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspace.id) }),
  });

  return (
    <div className="flex min-h-0 w-full flex-col bg-base">
      <div className="flex flex-none items-center gap-1 px-4 pt-3.5 pb-2.5">
        <h1 className="mr-auto fs-15 font-bold text-ink">Threads</h1>
        {canReview && (
          <button
            type="button"
            onClick={() => setNewRequestOpen(true)}
            title="New request, filed directly"
            aria-label="New request"
            className={ICON_BUTTON}
          >
            <PencilLine size={15} strokeWidth={1.75} aria-hidden />
          </button>
        )}
        <Link to="/new" title="New chat" aria-label="New chat" className={cx(ICON_BUTTON, 'text-ink')}>
          <SquarePen size={16} strokeWidth={1.75} aria-hidden />
        </Link>
      </div>

      {/* Live: the list narrows as you type. Esc clears it. */}
      <label className="mx-3 mb-2 flex flex-none items-center gap-2 rounded-lg bg-surface-2 px-2.5 focus-within:bg-surface-3">
        <Search size={13} strokeWidth={2} aria-hidden className="flex-none text-faint" />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setSearch('');
          }}
          placeholder="Search"
          aria-label="Search threads and replies"
          className="min-w-0 flex-1 bg-transparent py-1.5 fs-12.5 text-ink placeholder:text-faint focus:outline-none"
        />
      </label>

      <div className="flex flex-none items-center border-b border-surface-3 px-3">
        <div role="tablist" aria-label="View" className="flex items-center">
          {PRIMARY.map((option) => {
            const count =
              option.value === 'unread' ? unreadCount : option.value === 'mentions' ? mentionCount : 0;
            const selected = filter === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setFilter(option.value)}
                className={cx(
                  '-mb-px flex items-center gap-1 border-b-2 px-1.5 pt-1 pb-2 fs-12.5',
                  selected
                    ? 'border-ink font-semibold text-ink'
                    : 'border-transparent text-muted hover:text-ink',
                )}
              >
                {option.label}
                {count > 0 && (
                  <span
                    className={cx(
                      'numeric fs-11',
                      option.value === 'mentions' ? 'font-semibold text-accent' : 'text-faint',
                    )}
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center pb-1">
          {unreadCount > 0 && debounced === '' && (
            <button
              type="button"
              onClick={() => readAll.mutate()}
              disabled={readAll.isPending}
              title="Mark everything as read"
              aria-label="Mark all as read"
              className={cx(ICON_BUTTON, 'disabled:opacity-40')}
            >
              <CheckCheck size={15} strokeWidth={1.75} aria-hidden />
            </button>
          )}
          <FilterMenu
            options={secondaryOptions}
            value={filter}
            onChange={setFilter}
            active={activeSecondary !== undefined}
          />
        </div>
      </div>

      {activeSecondary !== undefined && (
        <div className="flex flex-none px-3 pt-2">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className="flex items-center gap-1 rounded-full bg-surface-3 py-0.5 pr-1.5 pl-2.5 fs-11.5 font-medium text-ink-soft hover:text-ink"
            aria-label={`Clear filter: ${activeSecondary.label}`}
          >
            {activeSecondary.label}
            <X size={12} strokeWidth={2} aria-hidden />
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5">
        {query.isPending ? (
          <p className="px-3 py-8 text-center fs-12 text-muted">Loading…</p>
        ) : query.error !== null ? (
          <p className="px-3 py-8 text-center fs-12 text-danger">{query.error.message}</p>
        ) : threads.length === 0 ? (
          <EmptyList
            filter={filter}
            search={debounced}
            onReadAll={unreadCount > 0 ? () => readAll.mutate() : null}
          />
        ) : (
          <>
            <ul className="space-y-px">
              {threads.map((thread) => (
                <ThreadRow key={thread.id} thread={thread} />
              ))}
            </ul>
            <LoadMore
              hasMore={query.hasNextPage}
              isLoading={query.isFetchingNextPage}
              onLoadMore={() => void query.fetchNextPage()}
              className="px-3 py-3"
            />
          </>
        )}
      </div>

      {canReview && (
        <NewRequestDialog workspaceId={workspace.id} open={newRequestOpen} onOpenChange={setNewRequestOpen} />
      )}
    </div>
  );
}

function FilterMenu({
  options,
  value,
  onChange,
  active,
}: {
  options: readonly { readonly value: ThreadFilter; readonly label: string }[];
  value: ThreadFilter;
  onChange: (next: ThreadFilter) => void;
  active: boolean;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        title="Filter"
        aria-label="Filter threads"
        className={cx(ICON_BUTTON, active && 'bg-surface-3 text-ink')}
      >
        <ListFilter size={15} strokeWidth={1.75} aria-hidden />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-[11rem] rounded-lg border border-line bg-base p-1 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.25)]"
        >
          <DropdownMenu.RadioGroup value={value} onValueChange={(next) => onChange(next as ThreadFilter)}>
            {options.map((option) => (
              <DropdownMenu.RadioItem
                key={option.value}
                value={option.value}
                className="flex cursor-default items-center gap-2 rounded-md py-1.5 pr-2 pl-7 fs-12.5 text-ink outline-none select-none data-[highlighted]:bg-surface-3 relative"
              >
                <DropdownMenu.ItemIndicator className="absolute left-2">
                  <Check size={13} strokeWidth={2.25} aria-hidden />
                </DropdownMenu.ItemIndicator>
                {option.label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function EmptyList({
  filter,
  search,
  onReadAll,
}: {
  filter: ThreadFilter;
  search: string;
  onReadAll: (() => void) | null;
}) {
  if (search !== '') {
    return <p className="px-3 py-8 text-center fs-12 text-muted">Nothing matches “{search}”.</p>;
  }
  const text: Record<ThreadFilter, string> = {
    all: 'No threads yet. Start one with the pen above.',
    unread: 'You’re all caught up.',
    mentions: 'Nobody has mentioned you yet.',
    chats: 'No chats yet.',
    issues: 'No issues yet.',
    following: 'You aren’t following anything. Threads you start, reply to or are mentioned in show up here.',
    review: 'Nothing is waiting for review.',
    archived: 'Nothing archived. Right-click a thread to archive it.',
  };
  return (
    <div className="px-3 py-8 text-center fs-12 text-muted">
      <p>{text[filter]}</p>
      {filter === 'unread' && onReadAll !== null && (
        <button type="button" onClick={onReadAll} className="mt-2 font-semibold text-accent hover:underline">
          Mark all as read
        </button>
      )}
    </div>
  );
}

/** One thread in the list. Right-click, or the `⋯` that appears on hover, for its actions. */
function ThreadRow({ thread }: { thread: ThreadSummary }) {
  const internal = thread.visibleToClient === false;
  const last = thread.lastMessage;
  const { actions, dialogs } = useThreadActions(thread);

  return (
    <>
      <ActionContextMenu actions={actions}>
        <li className="group relative">
          <NavLink
            to={`/t/${thread.id}`}
            className={({ isActive }) =>
              cx('flex gap-2 rounded-lg py-2 pr-2.5 pl-1.5', isActive ? 'bg-surface-3' : 'hover:bg-surface-2')
            }
          >
            <span className="flex w-2.5 flex-none justify-center pt-[0.4375rem]">
              {thread.unread && <span className="size-[7px] rounded-full bg-accent" aria-label="Unread" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline gap-1.5">
                <RowMarker thread={thread} />
                <span
                  className={cx(
                    'min-w-0 flex-1 truncate fs-13',
                    thread.unread ? 'font-semibold text-ink' : 'text-ink-soft',
                  )}
                >
                  {thread.title}
                </span>
                <span className="flex-none fs-11 text-faint group-hover:invisible">
                  {formatRelativeTime(thread.lastActivityAt)}
                </span>
              </span>
              <span className="mt-0.5 flex items-center gap-1.5 fs-12 text-muted">
                {internal && (
                  <Lock size={11} strokeWidth={2} className="flex-none text-violet" aria-label="Internal" />
                )}
                {thread.mentioned && (
                  <AtSign
                    size={11}
                    strokeWidth={2.5}
                    className="flex-none text-accent"
                    aria-label="You were mentioned"
                  />
                )}
                <span className="min-w-0 flex-1 truncate">
                  {last !== null ? (
                    <>
                      {last.authorKind === 'bot' ? '@rundown' : firstName(last.authorName)}:{' '}
                      {last.snippet || 'shared a file'}
                    </>
                  ) : (
                    'No messages yet'
                  )}
                </span>
                {thread.kind === 'issue' && thread.status !== null && !thread.filing && (
                  <span className="flex-none fs-11 text-faint">{STATUS_LABELS[thread.status]}</span>
                )}
              </span>
            </span>
          </NavLink>
          {/* Outside the link — a button inside an anchor is invalid markup and
              every press on it would navigate. */}
          <ActionMenuButton
            actions={actions}
            label="Thread actions"
            className="absolute top-1.5 right-1.5 bg-surface-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          />
        </li>
      </ActionContextMenu>
      {dialogs}
    </>
  );
}

/** "Talha Khatri" → "Talha": a list of snippets does not need everyone's full name. */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/**
 * Chat or issue, quietly: a speech bubble for conversation, the number in its
 * type's colour for work, and a soft pulse while @rundown is still filing it.
 */
function RowMarker({ thread }: { thread: ThreadSummary }) {
  if (thread.kind === 'chat') {
    return (
      <MessageCircle
        size={12}
        strokeWidth={2}
        aria-label="Chat"
        className="flex-none self-center text-faint"
      />
    );
  }
  const tone = thread.type !== null ? TYPE_TONES[thread.type] : 'neutral';
  return (
    <span
      className={cx(
        'numeric flex-none fs-11.5 font-medium',
        thread.filing ? 'animate-pulse text-accent' : (TONE_TEXT[tone] ?? 'text-muted'),
      )}
      title={thread.filing ? '@rundown is filing this' : undefined}
    >
      {thread.number !== null ? `#${thread.number}` : '#'}
    </span>
  );
}

/** Chat or issue, as a labelled badge — for a thread's own header and the ⌘K palette, where there is room. */
export function KindBadge({
  thread,
}: {
  thread: Pick<ThreadSummary, 'kind' | 'filing' | 'number' | 'type'>;
}) {
  if (thread.kind === 'chat') {
    return (
      <span className="flex flex-none items-center gap-1 rounded-full bg-surface-3 px-1.5 py-px fs-10.5 font-semibold text-muted">
        <MessageCircle size={10} strokeWidth={2.25} aria-hidden />
        Chat
      </span>
    );
  }
  if (thread.filing) {
    return (
      <span className="flex-none animate-pulse rounded-full bg-accent-tint px-1.5 py-px fs-10.5 font-semibold text-accent">
        {thread.number !== null ? `#${thread.number}` : 'Issue'} · filing
      </span>
    );
  }
  const tone = thread.type !== null ? TYPE_TONES[thread.type] : 'neutral';
  return (
    <span
      className={cx(
        'numeric flex-none rounded-full px-1.5 py-px fs-10.5 font-semibold',
        TONE_BADGE[tone] ?? TONE_BADGE['neutral'],
      )}
    >
      #{thread.number}
    </span>
  );
}
